import S from 's-js';
import { lift } from 's-array';
import * as _ from 'underscore';
import { IAjax } from 'lib/Ajax';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { callAll } from 'lib/callAll';
import { debounceSignal } from 'lib/debounceSignal';
import TZDate from 'lib/TZDate';
import Currency from 'lib/Currency';
import { CaseInsensitiveContains } from 'lib/diacriticals';

import { downloadDataUri } from 'common/services/downloadData';

import { Entrypoints } from 'core/app/entrypoints';

import { PCCache } from 'data/PCCache';

import { BillCoverageType } from 'data/models/BillCoverageType';
import { BillingSystem } from 'data/models/BillingSystem';
import { BillItem } from 'data/models/BillItem';
import { DomainSummary } from 'data/models/DomainSummary';
import { ExportPayloadStatus } from 'server/enums/ExportPayload';
import { PastExportsResponse, PastExportsResponseOptions, PastExportsBill } from 'data/models/PastExportsResponse';
import { QuickBooksReexportRequestData } from 'data/models/QuickBooksReexportRequest';
import { QuickBooksReexportResponseData } from 'data/models/QuickBooksReexportResponse';
import { TotalPriceForCharges } from 'data/models/Charge';

import { DateRangeSelector, DateRangeSelectorParams, DateRange } from 'portcall/daterangeselector/model';
import { RowSelector, RowExpander, RowExpansionMode } from 'portcall/gridcomponents/model';

import { PageAction } from './pagestateenums';
import { ExportGroupList, ExportGroupListParams, ExportGroupOptions, FilterData, PRICE_PRECISION } from './exportgrouplist';

const FilterStorageKey = 'portcall.billing.pastexports.filter';

export interface PastExportsParams {
    id : number;
    expansionMode : RowExpansionMode;
    range : DateRange;
    from? : TZDate;
    to? : TZDate;
    filter? : string;
}

export function PastExports(
    loader : CachedModelLoader<PastExportsResponse, PastExportsResponseOptions>,
    ajax : IAjax,
    entrypoints : Entrypoints,
    cache : PCCache,
    domain : DomainSummary,
    dateRangeSelectorModel : (params : DateRangeSelectorParams) => DateRangeSelector,
    exportGroupListModel : (params : ExportGroupListParams) => ExportGroupList,
    { id, expansionMode: _expansionMode, range, from, to, filter: _filter } : PastExportsParams
) {
    const dateRangeSelector = dateRangeSelectorModel({ range, from, to }),
        key = () => ({
            Id: id,
            From: dateRangeSelector.FromDate().toJSON(),
            To: dateRangeSelector.ToDate().toJSON()
        }),
        load = S(() => loader(key())),
        model = () => load().model,
        loading = () => load().loading(),
        billingSystem = cache.BillingSystems.update1(null, { Id: id }),
        filter = S.data(_filter || localStorage[FilterStorageKey] as string | undefined || ''),
        debouncedFilter = debounceSignal(filter),
        pageAction = S.data(PageAction.View),
        showActionsMenu = S.data(false),
        showConfirmExport = S.data(false),
        expansionMode = S.data(_expansionMode),
        billExportGroups = lift(() => model().Bills())
            .map(b => BillExportGroup(expansionMode, b)),
        exportGroups = billExportGroups.map(g => g.ExportGroup),
        filteredExportGroups = exportGroups
            .filter(g => CaseInsensitiveContains(FilterData(g), debouncedFilter())),
        exportGroupList = exportGroupListModel({
            BillingSystem: billingSystem,
            ExportGroups: filteredExportGroups,
            ShowGroupSelectors: () => pageAction() === PageAction.SelectForExport
        }),
        rowSelector = RowSelector(filteredExportGroups, g => g.SelectedForExport),
        rowExpander = RowExpander(expansionMode, exportGroups, filteredExportGroups, g => g.Expanded),
        exportConfirmationPopup = S.on(showConfirmExport, () => showConfirmExport() ? ExportConfirmationPopup(
            ajax,
            entrypoints,
            domain,
            billingSystem,
            billExportGroups(),
            () => pageAction(PageAction.View),
            () => showConfirmExport(false)
        ) : null);

    S.on(callAll(debouncedFilter, pageAction), () => exportGroups().forEach(g => g.SelectedForExport(false)), null, true);

    return {
        Model: model,
        Loading: loading,
        DateRangeSelector: dateRangeSelector,
        Filter: filter,
        PageAction: pageAction,
        RowExpander: rowExpander,
        ExportGroupList: exportGroupList,
        ShowActionsMenu: showActionsMenu,
        ShowConfirmExport: showConfirmExport,
        ExportConfirmationPopup: exportConfirmationPopup,
        RowSelector: rowSelector,
        MenuAction: (action : () => void) => () => S.freeze(() => {
            showActionsMenu(false);
            action();
        })
    };
}
export type PastExports = ReturnType<typeof PastExports>;

function BillExportGroup(
    expansionMode : () => RowExpansionMode,
    bill : PastExportsBill
) {
    const vessel = S(() => {
        const b = bill.Bill();
        if (!b) return null;
        const pjos = b.Coverage()
            .filter(c => c.Type() === BillCoverageType.JobOrder && c.JobOrder() && c.JobOrder()!.PilotJobOrder())
            .map(c => c.JobOrder()!.PilotJobOrder()!);
        // TH 05/11/2017 -- We'll assume that if a Bill has any PJOs, then they all have the same vessel
        return pjos.length ? pjos[0].EffectiveVessel() : null;
    });

    return {
        Bill: bill,
        ExportGroup: {
            InvoiceNumber: () => bill.Bill() ? bill.Bill()!.InvoiceNumber() : '',
            VesselName: () => vessel() && vessel()!.Name(),
            QBCustomerId: () => bill.Bill() && bill.Bill()!.QBTracking.Customer() || '',
            InvoiceDate: () => bill.Bill() && bill.Bill()!.InvoiceDate(),
            ExportDate: bill.LastExportDate,
            UniqueKey: () => '' + bill.Id(),
            Charges: lift(() => bill.Bill() ? bill.Bill()!.Items() : []),
            SelectedForExport: S.data(false),
            Expanded: S.data(S.sample(expansionMode) === RowExpansionMode.AutoExpand)
        }
    };
}
type BillExportGroup = ReturnType<typeof BillExportGroup>;

export type ExportFinishedStatus = { success: true } | { success: false, errorDetails: string };

function ExportConfirmationPopup(
    ajax : IAjax,
    entrypoints: Entrypoints,
    domain : DomainSummary,
    billingSystem : BillingSystem,
    billExportGroups : BillExportGroup[],
    onExportFinished : () => void,
    onCancel : () => void
) {
    const selectedBillGroups = billExportGroups.filter(g => g.ExportGroup.SelectedForExport()),
        allCharges = selectedBillGroups.reduce((charges, b) =>
            charges.concat(b.ExportGroup.Charges()), [] as BillItem[]),
        exportTotal = TotalPriceForCharges(lift(() => allCharges), () => billingSystem.Currency() || Currency.USD, PRICE_PRECISION),
        exportClient = ajax.Url<QuickBooksReexportRequestData, QuickBooksReexportResponseData>(() => entrypoints.QuickBooksReexport({ Id: billingSystem.Id() })),
        sendingExportRequest = S.data(false),
        exportFinishedStatus = S.data<ExportFinishedStatus | null>(null);

    return {
        NumberOfInvoices: selectedBillGroups.length,
        ExportTotal: exportTotal,
        Cancel: onCancel,
        SendExportRequest,
        SendingExportRequest: sendingExportRequest,
        ExportFinishedStatus: exportFinishedStatus,
        SupportHref: () => 'mailto:' + domain.SupportEmail()
    };

    async function SendExportRequest() {
        if (sendingExportRequest()) return;

        sendingExportRequest(true);
        const req = exportClient.Post({
            BillIds: selectedBillGroups.map(g => g.Bill.Id())
        });
        try {
            const resp = await req;
            S.freeze(() => {
                if (resp.Payload!.Status === ExportPayloadStatus.Formatted) {
                    const dataUri = `data:text/plain;charset=utf-8,${encodeURIComponent(resp.Payload!.RawExport!)}`;
                    downloadDataUri(dataUri, 'invoices.iif');
                    exportFinishedStatus({ success: true });
                } else {
                    // TH 05/11/2017 -- As for exports of new invoices, we'll handle the possibility that
                    // formatting might fail.
                    exportFinishedStatus({
                        success: false,
                        errorDetails: resp.Payload!.ErrorMessage || 'Unexpected export error.'
                    });
                }

                // TH 05/11/2017 -- Update our LastExportDate.
                // As for exports of new invoices, we won't cache the ExportPayload itself.
                if (resp.NewExportDate !== null) selectedBillGroups.forEach(g => g.Bill.LastExportDate(new TZDate(resp.NewExportDate!)));
                onExportFinished();
            });
        } finally {
            sendingExportRequest(false);
        }
    }
}
export type ExportConfirmationPopup = ReturnType<typeof ExportConfirmationPopup>;



// WEBPACK FOOTER //
// ./apps/portcall/quickbooksexports/pastexports.ts